import React, { lazy, Suspense, useState, useEffect } from "react"
import { graphql } from "gatsby"
import { kebabCase } from "lodash"

/* webpackPrefetch will ask the browser to start fetching it asap */
//lazy loading all pages sections
const lazyMap = {
  "sections.image-text": lazy(() =>
    import(
      /* webpackPrefetch: true */
      "../sections/ImageText/ImageTextSection"
    )
  ),
  "sections.image": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Image")
  ),
  "sections.cta": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Cta")
  ),
  "sections.cta-info-cards": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/CtaInfoCards")
  ),
  "sections.info-cards": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/InfoCards")
  ),
  "sections.twitter": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Twitter")
  ),
  "sections.button-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ButtonGrid")
  ),
  "sections.blog-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/BlogGrid")
  ),
  "sections.newsletter-signup": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/NewsletterSignup")
  ),
  "sections.team-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/TeamGrid")
  ),
  "sections.team-member": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/TeamMember")
  ),
  "sections.logo-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/LogoGrid")
  ),
  "sections.image-carousel": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ImageCarousel")
  ),
  "sections.stats": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Stats")
  ),
  "sections.contact": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Contact")
  ),
  "sections.careers": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Careers")
  ),
  "sections.careers-ats": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/CareersAts")
  ),
  "sections.pricing": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Pricing")
  ),
  "sections.pricing-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/PricingGrid")
  ),
  "sections.contact-form": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ContactForm")
  ),
  "sections.dynamic-title": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/DynamicTitle")
  ),
  "sections.logo-carousel": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/LogoCarousel")
  ),
  "sections.custom-html": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/CustomHtml")
  ),
  "sections.portfolio-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/PortfolioGrid")
  ),
  "sections.article": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Article")
  ),
  "sections.quote-carousel": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/QuoteCarousel")
  ),
  "sections.image-text-carousel": lazy(() =>
    import(
      /* webpackPrefetch: true */
      "../sections/ImageTextCarousel/ImageTextCarousel"
    )
  ),
  "sections.faqs": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Faqs")
  ),
  "sections.image-grid": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ImageGrid")
  ),
  "sections.image-cards": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ImageCards")
  ),
  "sections.big-image-cards": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/BigImageCards")
  ),
  "sections.interactive-info-cards": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/InteractiveInfoCards")
  ),
  "sections.text": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/Text")
  ),
  "sections.thin-cta": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ThinCta")
  ),
  "sections.flex-columns": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/FlexColumns")
  ),
  "sections.toggle-list": lazy(() =>
    import(/* webpackPrefetch: true */ "../sections/ToggleList")
  ),
}

// helper to turn Strapi typename into the key we use in lazyMap
const mapTypeToKey = section => {
  if (section.__component) {
    return section.__component.startsWith("sections.")
      ? section.__component // Next.js shape
      : `sections.${section.__component}` // Gatsby shape
  }
  if (section.__typename)
    return `sections.${kebabCase(section.__typename.slice(27))}`
  return null
}

export const getSectionId = section => {
  if (section.__component && section.strapi_id)
    return `sections.${section.__component}.${section.strapi_id}`
  if (section.__component && typeof section.id === "number")
    return `${section.__component}.${section.id}`
  if (section.__component && typeof section.id === "string") return section.id // when section has placeholder id, the __component value isn't included in id value in redux state
  if (section.__typename)
    return `sections.${kebabCase(section.__typename.slice(27))}.${
      section.id || section.strapi_id
    }`
  // slice(27) removes "STRAPI__COMPONENT_SECTIONS_"
  else return ""
}

const Sections = ({ section, index = 0, ssrCount = 0 }) => {
  const [ready, setReady] = useState(false)
  useEffect(() => setReady(true), [])
  if (!ready) {
    // placeholder only on the very first client render
    return <div suppressHydrationWarning style={{ minHeight: 300 }} />
  }

  // skip section if set to be hidden
  if (section.section?.hidden) return null

  const key = mapTypeToKey(section)
  const LazyComponent = key ? lazyMap[key] : null
  if (!LazyComponent) return null // return early if section is not found

  const id = getSectionId(section)
  if (section.section) section.section.sectionId = id

  const eager = index < ssrCount
  // const fallback = <PageSpinner />
  /* light skeleton (no spinner) */
  const fallback = <div style={{ minHeight: 300 }} aria-busy="true" />

  return (
    <section id={id} style={{ margin: 0, padding: 0 }} suppressHydrationWarning>
      <Suspense fallback={eager ? null : fallback}>
        <LazyComponent {...section} eagerLoad={eager && index === 0} />
      </Suspense>
    </section>
  )
}

export default Sections

export const query = graphql`
  fragment Sections on PageSectionsDynamicZone {
    __typename
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_TEXT {
      ...ImageTextSection
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE {
      ...ImageSection
    }
    ... on STRAPI__COMPONENT_SECTIONS_CTA {
      ...Cta
    }
    ... on STRAPI__COMPONENT_SECTIONS_TWITTER {
      ...Twitter
    }
    ... on STRAPI__COMPONENT_SECTIONS_INFO_CARDS {
      ...InfoCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_BUTTON_GRID {
      ...ButtonGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_BLOG_GRID {
      ...BlogGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_NEWSLETTER_SIGNUP {
      ...NewsletterSignup
    }
    ... on STRAPI__COMPONENT_SECTIONS_TEAM_GRID {
      ...TeamGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_TEAM_MEMBER {
      ...TeamMember
    }
    ... on STRAPI__COMPONENT_SECTIONS_LOGO_GRID {
      ...LogoGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_CAROUSEL {
      ...ImageCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_STATS {
      ...Stats
    }
    ... on STRAPI__COMPONENT_SECTIONS_CONTACT {
      ...Contact
    }
    ... on STRAPI__COMPONENT_SECTIONS_CAREERS {
      ...Careers
    }
    ... on STRAPI__COMPONENT_SECTIONS_CAREERS_ATS {
      ...CareersAts
    }
    ... on STRAPI__COMPONENT_SECTIONS_PRICING {
      ...Pricing
    }
    ... on STRAPI__COMPONENT_SECTIONS_PRICING_GRID {
      ...PricingGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_CONTACT_FORM {
      ...ContactForm
    }
    ... on STRAPI__COMPONENT_SECTIONS_DYNAMIC_TITLE {
      ...DynamicTitle
    }
    ... on STRAPI__COMPONENT_SECTIONS_LOGO_CAROUSEL {
      ...LogoCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_CUSTOM_HTML {
      ...CustomHtml
    }
    ... on STRAPI__COMPONENT_SECTIONS_PORTFOLIO_GRID {
      ...PortfolioGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_ARTICLE {
      ...Article
    }
    ... on STRAPI__COMPONENT_SECTIONS_QUOTE_CAROUSEL {
      ...QuoteCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_TEXT_CAROUSEL {
      ...ImageTextCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_FAQS {
      ...Faqs
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_GRID {
      ...ImageGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_CARDS {
      ...ImageCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_BIG_IMAGE_CARDS {
      ...BigImageCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_CTA_INFO_CARDS {
      ...CtaInfoCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_INTERACTIVE_INFO_CARDS {
      ...InteractiveInfoCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_TEXT {
      ...Text
    }
    ... on STRAPI__COMPONENT_SECTIONS_THIN_CTA {
      ...ThinCta
    }
    ... on STRAPI__COMPONENT_SECTIONS_FLEX_COLUMNS {
      ...FlexColumns
    }
    ... on STRAPI__COMPONENT_SECTIONS_TOGGLE_LIST {
      ...ToggleList
    }
  }
`
